import React, { useEffect, useMemo, useState } from 'react';
import { Button, Result, Spin, Typography, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getActiveDivisions } from 'app/shared/api/getDivision';
import {
  getPricingYearsByDivision,
  DivisionPricingYear,
  isDraftPricingYear,
} from 'app/shared/api/getYears';
import { loadDraftOfferings } from 'app/shared/api/draftPricingYear';
import { getCentres } from 'app/shared/api/getCentres';
import { getSchoolsByCentre } from 'app/shared/api/getSchools';
import {
  getSelectedDivisionIdOrUndefined,
  setSelectedDivisionId as persistSelectedDivisionId,
} from 'app/shared/division/selectedDivisionStorage';

const { Title, Paragraph, Text } = Typography;

const readSelectedDivisionId = (): number | undefined => getSelectedDivisionIdOrUndefined();

const isLockedPricingYear = (py: DivisionPricingYear): boolean => py.isLocked === true;

const isEditableDraftYear = (py: DivisionPricingYear): boolean =>
  isDraftPricingYear(py) && !isLockedPricingYear(py);

const hasDraftPricingYear = (pricingYears: DivisionPricingYear[]): boolean =>
  pricingYears.some(isEditableDraftYear);

const formatPricingYearLabel = (py: DivisionPricingYear): string => {
  if (isLockedPricingYear(py)) {
    return `${py.year} (Locked due to publishing...)`;
  }
  if (isDraftPricingYear(py)) {
    return `${py.year} (Draft)`;
  }
  return String(py.year);
};

const Page: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDivisionId, setSelectedDivisionIdState] = useState<number | undefined>(
    readSelectedDivisionId()
  );
  const [divisionName, setDivisionName] = useState<string>('');
  const [pricingYears, setPricingYears] = useState<DivisionPricingYear[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftExists, setDraftExists] = useState<boolean | null>(null);
  const [draftYearFromApi, setDraftYearFromApi] = useState<number | null>(null);

  const emptyStateDescription = useMemo(() => {
    if (!divisionName) {
      return (
        <>
          No draft Pricing Year exists for the selected division.
          <br />
          You can create a new draft Pricing Year to begin configuration.
        </>
      );
    }

    return (
      <>
        No draft Pricing Year exists for <strong>{divisionName}</strong>.
        <br />
        You can create a new draft Pricing Year to begin configuration.
      </>
    );
  }, [divisionName]);

  const sortedPricingYears = useMemo(
    () => [...pricingYears].sort((a, b) => a.year - b.year),
    [pricingYears]
  );

  const editableDraftYears = useMemo(
    () => sortedPricingYears.filter(isEditableDraftYear),
    [sortedPricingYears]
  );

  const uniqueYears = useMemo(() => {
    const years = pricingYears
      .map((py) => py.year)
      .filter((v): v is number => typeof v === 'number');
    return Array.from(new Set(years)).sort((a, b) => a - b);
  }, [pricingYears]);

  const nextPricingYear = useMemo(() => {
    const fallbackBase = new Date().getFullYear();
    if (!uniqueYears.length) {
      return fallbackBase + 1;
    }
    const maxYear = Math.max(...uniqueYears);
    return Math.max(maxYear + 1, fallbackBase + 1);
  }, [uniqueYears]);

  const disableStartNewPricingYear = useMemo(
    () => draftExists === true || editableDraftYears.length > 0,
    [draftExists, editableDraftYears.length]
  );

  const openDraft = React.useCallback(() => {
    if (!selectedDivisionId) return;
    const year =
      draftYearFromApi ??
      pricingYears.find(isEditableDraftYear)?.year ??
      nextPricingYear - 1;
    persistSelectedDivisionId(selectedDivisionId);
    navigate('/pricelist/draft', {
      state: { divisionId: selectedDivisionId, year },
    });
  }, [selectedDivisionId, draftYearFromApi, pricingYears, nextPricingYear, navigate]);

  const existingYearsSection = useMemo(() => {
    if (!selectedDivisionId) {
      return null;
    }

    const hasYears = sortedPricingYears.length > 0;

    return (
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Existing Pricing Years
        </Text>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          {sortedPricingYears.map((py) => {
            const isEditableDraft = isEditableDraftYear(py);
            const handleDraftClick =
              isEditableDraft && draftExists ? openDraft : undefined;
            return (
              <Tag
                key={py.id}
                color={isEditableDraft ? 'blue' : undefined}
                style={handleDraftClick ? { cursor: 'pointer' } : undefined}
                onClick={handleDraftClick}
                onKeyDown={
                  handleDraftClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDraftClick();
                        }
                      }
                    : undefined
                }
                role={handleDraftClick ? 'button' : undefined}
                tabIndex={handleDraftClick ? 0 : undefined}
              >
                {formatPricingYearLabel(py)}
              </Tag>
            );
          })}
        </div>
        {!hasYears && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            No pricing years found for this division yet.
          </Text>
        )}
      </div>
    );
  }, [selectedDivisionId, sortedPricingYears, draftExists, openDraft]);

  useEffect(() => {
    const syncDivision = () => {
      setSelectedDivisionIdState(readSelectedDivisionId());
    };
    window.addEventListener('selectedDivisionChanged', syncDivision);
    return () => window.removeEventListener('selectedDivisionChanged', syncDivision);
  }, []);

  useEffect(() => {
    if (!selectedDivisionId) {
      setDivisionName('');
      setPricingYears([]);
      setDraftExists(null);
      setDraftYearFromApi(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [divisions, divisionPricingYears] = await Promise.all([
          getActiveDivisions(),
          getPricingYearsByDivision(selectedDivisionId),
        ]);

        if (cancelled) return;

        const selectedDivision = divisions.find((division) => division.id === selectedDivisionId);
        setDivisionName(selectedDivision?.name ?? 'this division');
        setPricingYears(divisionPricingYears ?? []);

        const years = (divisionPricingYears ?? [])
          .map((item) => item?.year)
          .filter((v): v is number => typeof v === 'number');
        const maxYear = years.length ? Math.max(...years) : new Date().getFullYear();
        const draftYearCandidate = maxYear + 1;

        let hasDraft = hasDraftPricingYear(divisionPricingYears ?? []);
        let resolvedDraftYear: number | null = null;

        try {
          const centres = await getCentres();
          if (cancelled) return;
          const firstCentreId = centres?.[0]?.id ?? centres?.[0];
          if (firstCentreId != null) {
            const schools = await getSchoolsByCentre(firstCentreId);
            if (cancelled) return;
            const firstSchool = schools?.[0];
            const schoolId =
              firstSchool &&
              (typeof (firstSchool as any).id === 'number'
                ? (firstSchool as any).id
                : /^School-(\d+)$/i.test(String((firstSchool as any).id))
                  ? parseInt(/^School-(\d+)$/i.exec(String((firstSchool as any).id))![1], 10)
                  : parseInt(String((firstSchool as any).id), 10));
            if (Number.isFinite(schoolId)) {
              const result = await loadDraftOfferings(selectedDivisionId, {
                year: draftYearCandidate,
                schoolIds: [{ id: schoolId }],
                productType: 'Accommodation',
              });
              if (cancelled) return;
              if (result.ok && Array.isArray(result.data) && result.data.length > 0) {
                hasDraft = true;
                resolvedDraftYear = draftYearCandidate;
              }
            }
          }
        } catch (_) {
        }

        if (!hasDraft && (divisionPricingYears ?? []).some(isEditableDraftYear)) {
          const draftRecord = (divisionPricingYears ?? []).find(isEditableDraftYear);
          resolvedDraftYear = draftRecord?.year ?? null;
        }

        if (!cancelled) {
          setDraftExists(hasDraft);
          setDraftYearFromApi(resolvedDraftYear);
        }
      } catch (err) {
        console.error('Failed to load pricing year information', err);
        if (!cancelled) {
          setError('We were unable to check the pricing year status. Please try again.');
          setPricingYears([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedDivisionId]);

  const handleStart = () => {
    navigate('/new-pricing-year/wizard', { state: { pricingYear: nextPricingYear } });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Unable to Load"
        subTitle={error}
      />
    );
  }

  if (!selectedDivisionId) {
    return (
      <Result
        status="info"
        title="Select a Division"
        subTitle="Choose a division from the top-right selector to create a new pricing year."
      />
    );
  }

  if (draftExists) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ maxWidth: 560, textAlign: 'center' }}>
          <Title level={3} style={{ marginBottom: 16 }}>
            Draft Pricing Year
          </Title>
          <Paragraph style={{ fontSize: 16, marginBottom: 24 }}>
            {divisionName ? (
              <>You have a draft pricing year for <strong>{divisionName}</strong>. Click the draft year to continue.</>
            ) : (
              <>You have a draft pricing year. Click the draft year to continue.</>
            )}
          </Paragraph>
          {existingYearsSection}
          <Button type="primary" size="large" onClick={handleStart} disabled={disableStartNewPricingYear} style={{ marginTop: 24 }}>
            Create Draft Pricing Year
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ maxWidth: 560, textAlign: 'center' }}>
        <Title level={3} style={{ marginBottom: 16 }}>
          No Draft Pricing Year Found
        </Title>
        <Paragraph style={{ fontSize: 16, marginBottom: 24 }}>
          {emptyStateDescription}
        </Paragraph>
        {existingYearsSection}
        <Button type="primary" size="large" onClick={handleStart} disabled={disableStartNewPricingYear} style={{ marginTop: 24 }}>
          Create Draft Pricing Year
        </Button>
      </div>
    </div>
  );
};

export default Page;